// The network came back (#397): what failed to read while offline reads again.
// One signal per process. Each app announces it from the listener it already
// has (mobile: the outbox bridge; web: the window's `online`); the read hooks
// subscribe.

const listeners = new Set<() => void>();

/** Calls `fn` on each offline to online transition. Returns the unsubscribe. */
export const onReconnect = (fn: () => void): (() => void) => {
  listeners.add(fn);
  return () => {
    listeners.delete(fn);
  };
};

export const announceReconnect = (): void => {
  for (const fn of [...listeners]) fn();
};

// The app came back to the front (#509): what it holds may have aged while it
// was away. Same shape as the reconnect: each app announces it from the
// listener it has (mobile: AppState 'active'; web: visibilitychange to
// visible); the read hooks subscribe.
const foregroundListeners = new Set<() => void>();

/** Calls `fn` each time the app returns to the foreground. Returns the unsubscribe. */
export const onForeground = (fn: () => void): (() => void) => {
  foregroundListeners.add(fn);
  return () => {
    foregroundListeners.delete(fn);
  };
};

export const announceForeground = (): void => {
  for (const fn of [...foregroundListeners]) fn();
};

/** The part of an AbortSignal `whenOnline` uses (no DOM types here). */
export type StopSignal = {
  readonly aborted: boolean;
  addEventListener(type: "abort", fn: () => void): void;
  removeEventListener(type: "abort", fn: () => void): void;
};

/** Now when `offline()` is false, else at the next reconnect, or once `stop`
 *  aborts: the waiter leaves the listeners then, so a flow cancelled while
 *  offline (a boot torn down) does not stay subscribed. */
export const whenOnline = (offline: () => boolean, stop?: StopSignal): Promise<void> => {
  if (!offline() || stop?.aborted === true) return Promise.resolve();
  return new Promise((resolve) => {
    const done = () => {
      off();
      stop?.removeEventListener("abort", done);
      resolve();
    };
    const off = onReconnect(done);
    stop?.addEventListener("abort", done);
  });
};
